/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { When } from "react-if";
import {
  Route as NativeRoute,
  Routes,
  Navigate,
  Route,
} from "react-router-dom";
import styled from "styled-components";
import { Container, Spacer, theme } from "@nordcloud/gnui";
import { ErrorFallback, GlobalSpinner } from "~/components";

import { useFeature } from "~/hooks";
import { Footer, Header, MenuBar, SidebarLayout } from "~/layout";

import { NoCustomerAccess } from "~/layout/NoCustomerAccess";
import { ACTIONS, Can, useAccess } from "~/services/auth";

import { useCustomers } from "~/services/customers";
import { lazyWithRetry } from "~/tools";
import { Page403 } from "~/views/403";
import { Page404 } from "~/views/404";
import { AccountAddPage } from "~/views/accounts/AccountAddPage";
import { AccountDetailsPage } from "~/views/accounts/AccountDetails";
import { AccountEditPage } from "~/views/accounts/AccountEditPage";
import { AccountsPage } from "~/views/accounts/AccountsPage";
import { AddCapacityPage } from "~/views/accounts/AddCapacity";
import { ApplicationsCostAnalysisPage } from "~/views/applicationCostAnalysis/ApplicationsCostAnalysisPage";
import { AddApplicationPage } from "~/views/applications/AddApplicationPage";
import { ApplicationDetailsPage } from "~/views/applications/ApplicationDetailsPage";
import {
  ApplicationsPage,
  ApplicationsPageV2,
} from "~/views/applications/ApplicationsPage";
import { Archive } from "~/views/archive";
import { BusinessMenu, OrganizationUnitTypes } from "~/views/business";
import {
  BusinessContextsStructure,
  OrganizationUnitPage,
} from "~/views/businessContexts";
import { CloudAccountDetailsPage } from "~/views/cloudAccounts/CloudAccountDetails/CloudAccountDetailsPage";
import { CloudAccountsPage } from "~/views/cloudAccounts/CloudAccountsPage";
import { DriftReportPage } from "~/views/drift/DriftReportPage";
import { EnvironmentDetails } from "~/views/environments/Details/EnvironmentDetails";
import { EstateDetailsPage } from "~/views/estate/EstateDetailsPage";
import { EstatePage } from "~/views/estate/EstatePage";
import { EstateRecordsPage } from "~/views/estate/EstateRecordsPage";
import { ExportPage } from "~/views/estate/ExportPage";
import { AddIntegration } from "~/views/externalIntegrations/AddIntegration";
import { ExternalIntegrationsDetailsPage } from "~/views/externalIntegrations/ExternalIntegrationsDetailsPage";
import { ExternalIntegrationsPage } from "~/views/externalIntegrations/ExternalIntegrationsPage";
import { FinopsKpiPage } from "~/views/finopsKpi";
import { HomePageAdmin, HomePageTeamLeader } from "~/views/home";
import { AddIntegrationPlugin } from "~/views/integrationPlugins/AddIntegrationPlugin";
import { IntegrationPluginsDetailsPage } from "~/views/integrationPlugins/IntegrationPluginsDetails";
import { IntegrationPluginsPage } from "~/views/integrationPlugins/IntegrationPluginsList";
import { NotificationPage } from "~/views/notifications/NotificationPage";
import { EmployeesPage } from "~/views/people";
import {
  AwsReservationsCommitmentsPage,
  AzureReservationsCommitmentsPage,
} from "~/views/reservedInstances";
import {
  CloudWasteSettings,
  CostSplittingRulesPage,
  MappingRulesPage,
  SettingsPage,
  UsersAndPermissionsPage,
} from "~/views/settings";
import { ApiKeysTable } from "~/views/settings/GlobalApiKeys";
import { ThresholdsPage } from "~/views/thresholds/ThresholdsPage";
import { WhatsNew } from "~/views/whatsnew/Whatsnew";
import { AddWorkflowPage } from "~/views/workflows/AddWorkflow";
import { WorkflowDetailsPage } from "~/views/workflows/WorkflowDetails";
import { WorkflowsPage } from "~/views/workflows/WorkflowsPage";
import { ROUTES } from "./routes";
import { AccessHandler, RequireAccess, renderMultiRoutes } from "./utils";

const layoutHorizontalSpacing = theme.spacing.spacing04;

const ApiDocsPage = lazyWithRetry(() => import("~/views/api"), "ApiDocsPage");

export function GlobalSwitch() {
  const { checkAccess } = useAccess();
  const { shouldShowNoCustomerMessage, isAdminLite } = useCustomers();
  const areThresholdsEnabled = useFeature("thresholds");
  const showCloudAccounts = useFeature("cloudAccounts");

  return (
    <Container
      fluid
      css={{
        paddingLeft: 0,
        paddingTop: 0,
        paddingRight: layoutHorizontalSpacing,
        width: "100%",
        height: "100%",
      }}
    >
      <ApplicationGrid>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MenuBar />
        <ApplicationWrapper>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<GlobalSpinner loading />}>
              <When condition={shouldShowNoCustomerMessage}>
                <NoCustomerAccess />
                <Spacer height={theme.spacing.spacing04} />
              </When>
              <Routes>
                <Route
                  path={ROUTES.index}
                  element={
                    isAdminLite ? (
                      <Navigate to={ROUTES.costAnalysis.index} />
                    ) : (
                      <Can
                        action={ACTIONS.visitHomepage}
                        fallback={<HomePageTeamLeader />}
                      >
                        <HomePageAdmin />
                      </Can>
                    )
                  }
                />
                <Route
                  path={ROUTES.export}
                  element={
                    <RequireAccess action={ACTIONS.visitExport}>
                      <ExportPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.applications.index}
                  element={
                    <RequireAccess action={ACTIONS.visitApplications}>
                      <ApplicationsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.applications.indexV2}
                  element={
                    <RequireAccess action={ACTIONS.visitApplications}>
                      <ApplicationsPageV2 />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.applications.create}
                  element={
                    <RequireAccess action={ACTIONS.visitApplicationAdd}>
                      <AddApplicationPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.applications.details}
                  element={
                    <RequireAccess action={ACTIONS.visitApplicationDetails}>
                      <ApplicationDetailsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.applications.environment}
                  element={
                    <RequireAccess action={ACTIONS.visitEnvironmentDetails}>
                      <EnvironmentDetails />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.resource}
                  element={
                    <RequireAccess action={ACTIONS.visitEstateRecordsDetails}>
                      <EstateDetailsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.estateRecords.index}
                  element={
                    <RequireAccess action={ACTIONS.visitEstateRecords}>
                      <EstatePage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.estateRecords.indexV2}
                  element={
                    <RequireAccess action={ACTIONS.visitEstateRecords}>
                      <EstateRecordsPage />
                    </RequireAccess>
                  }
                />

                <Route
                  path={ROUTES.reservedInstances.index}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitReservations)}
                    >
                      <AwsReservationsCommitmentsPage />
                    </AccessHandler>
                  }
                />

                <Route
                  path={ROUTES.reservedInstances.azureReservation}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitReservations)}
                    >
                      <AzureReservationsCommitmentsPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.externalIntegrations.index}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitExternalIntegrations)}
                    >
                      <ExternalIntegrationsPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.externalIntegrations.create}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitExternalIntegrations)}
                    >
                      <AddIntegration />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.externalIntegrations.details}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitExternalIntegrations)}
                    >
                      <ExternalIntegrationsDetailsPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.integrationPlugins.index}
                  element={<IntegrationPluginsPage />}
                />
                <Route
                  path={ROUTES.integrationPlugins.create}
                  element={<AddIntegrationPlugin />}
                />
                <Route
                  path={ROUTES.integrationPlugins.details}
                  element={<IntegrationPluginsDetailsPage />}
                />
                <Route
                  path={ROUTES.workflows.index}
                  element={
                    <RequireAccess action={ACTIONS.visitWorkflows}>
                      <WorkflowsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.workflows.create}
                  element={
                    <RequireAccess action={ACTIONS.visitWorkflowAdd}>
                      <AddWorkflowPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.workflows.details}
                  element={
                    <RequireAccess action={ACTIONS.visitWorkflowDetails}>
                      <WorkflowDetailsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.estateRecords.drift}
                  element={
                    <RequireAccess action={ACTIONS.visitDriftReport}>
                      <DriftReportPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.cloudAccounts.index}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitAccounts)}
                    >
                      <CloudAccountsPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.cloudAccounts.details}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitAccountDetails)}
                    >
                      <CloudAccountDetailsPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.thresholds.index}
                  element={
                    <AccessHandler
                      hasAccess={
                        isAdminLite &&
                        areThresholdsEnabled &&
                        checkAccess(ACTIONS.visitThresholds)
                      }
                    >
                      <ThresholdsPage />
                    </AccessHandler>
                  }
                />
                {/* Deprecated route */}
                <Route
                  path={ROUTES.accounts.index}
                  element={
                    <AccessHandler
                      hasAccess={
                        showCloudAccounts && checkAccess(ACTIONS.visitAccounts)
                      }
                    >
                      <AccountsPage />
                    </AccessHandler>
                  }
                />
                {/* Deprecated route */}
                <Route
                  path={ROUTES.accounts.add}
                  element={
                    <AccessHandler
                      hasAccess={
                        showCloudAccounts &&
                        checkAccess(ACTIONS.visitAccountAdd)
                      }
                    >
                      <AccountAddPage />
                    </AccessHandler>
                  }
                />
                {/* Deprecated route */}
                <Route
                  path={ROUTES.accounts.addBillingData}
                  element={
                    <AccessHandler
                      hasAccess={
                        showCloudAccounts &&
                        checkAccess(ACTIONS.visitAddBillingData)
                      }
                    >
                      <AddCapacityPage />
                    </AccessHandler>
                  }
                />
                {/* Deprecated route */}
                <Route
                  path={ROUTES.accounts.details}
                  element={
                    <AccessHandler
                      hasAccess={
                        showCloudAccounts &&
                        checkAccess(ACTIONS.visitAccountDetails)
                      }
                    >
                      <AccountDetailsPage />
                    </AccessHandler>
                  }
                />
                {/* Deprecated route */}
                <Route
                  path={ROUTES.accounts.edit}
                  element={
                    <AccessHandler
                      hasAccess={
                        showCloudAccounts &&
                        checkAccess(ACTIONS.visitAccountEdit)
                      }
                    >
                      <AccountEditPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.notifications.index}
                  element={
                    <RequireAccess action={ACTIONS.visitNotifications}>
                      <NotificationPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.settings.users}
                  element={
                    <RequireAccess action={ACTIONS.visitUsersPermissions}>
                      <UsersAndPermissionsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.settings.globalApiKeys}
                  element={
                    <RequireAccess action={ACTIONS.visitGlobalApiKeys}>
                      <ApiKeysTable />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.settings.costSplitting}
                  element={
                    <RequireAccess action={ACTIONS.visitCostSplittingRules}>
                      <CostSplittingRulesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.settings.mapping}
                  element={
                    <RequireAccess action={ACTIONS.visitMapping}>
                      <MappingRulesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.settings.cloudWaste}
                  element={
                    <RequireAccess action={ACTIONS.visitSavingsSuggestions}>
                      <CloudWasteSettings />
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.settings.employees}
                  element={
                    <RequireAccess action={ACTIONS.visitEmployees}>
                      <EmployeesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path={`${ROUTES.business.unitTypes}`}
                  element={
                    <SidebarLayout sidebar={<BusinessMenu />}>
                      <RequireAccess action={ACTIONS.visitOrgUnitTypes}>
                        <OrganizationUnitTypes />
                      </RequireAccess>
                    </SidebarLayout>
                  }
                />
                <Route
                  path={ROUTES.businessContexts.structure}
                  element={
                    <RequireAccess
                      action={ACTIONS.visitBusinessContextStructure}
                    >
                      <BusinessContextsStructure />
                    </RequireAccess>
                  }
                />
                {renderMultiRoutes({
                  paths: [
                    ROUTES.businessContexts.details,
                    ROUTES.businessContexts.detailsSimple,
                  ],
                  element: (
                    <RequireAccess action={ACTIONS.visitBusinessContextDetails}>
                      <OrganizationUnitPage />
                    </RequireAccess>
                  ),
                })}
                <Route
                  path={ROUTES.costAnalysis.index}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(ACTIONS.visitCostAnalysis)}
                    >
                      <ApplicationsCostAnalysisPage />
                    </AccessHandler>
                  }
                />
                <Route
                  path={ROUTES.finopsKpi.index}
                  element={
                    <AccessHandler
                      hasAccess={checkAccess(
                        ACTIONS.visitBusinessContextDetails
                      )}
                    >
                      <FinopsKpiPage />
                    </AccessHandler>
                  }
                />
                {renderMultiRoutes({
                  paths: [
                    ROUTES.settings.employees,
                    ROUTES.settings.reports,
                    ROUTES.settings.index,
                  ],
                  element: (
                    <RequireAccess action={ACTIONS.visitSettings}>
                      <SettingsPage />
                    </RequireAccess>
                  ),
                })}
                <Route
                  path={ROUTES.releaseNotes}
                  element={
                    <RequireAccess action={ACTIONS.visitReleaseNotes}>
                      <WhatsNew />
                    </RequireAccess>
                  }
                />
                <NativeRoute
                  // Native route is used because of frequent pathname updates inside `ApiDocsPage`, which caused a lot of network requests to datadog

                  path={ROUTES.api}
                  element={
                    <RequireAccess action={ACTIONS.visitApiDocs}>
                      <Suspense fallback={<GlobalSpinner loading />}>
                        <ApiDocsPage />
                      </Suspense>
                    </RequireAccess>
                  }
                />
                <Route
                  path={ROUTES.archive}
                  element={
                    <RequireAccess action={ACTIONS.visitArchive}>
                      <Archive />
                    </RequireAccess>
                  }
                />
                <Route path={ROUTES.accessDenied} element={<Page403 />} />
                <Route path="*" element={<Page404 />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </ApplicationWrapper>
        <FooterWrapper>
          <Footer />
        </FooterWrapper>
      </ApplicationGrid>
    </Container>
  );
}

// Cache menu bar & layout dimensions
const menuWidth = theme.spacing.spacing07;
const menuHorizontalPadding = theme.spacing.spacing03;

// `1st calc`: we need to add menuHorizontalPadding twice because we have
// PaddingLeft and paddingRight on the menu element

// `2nd calc`: we need to add layoutHorizontalSpacing additionally to mitigate
// Issue with right padding on global application container which is wrongly
// Interpreted by the grid
const ApplicationGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.spacing04} ${layoutHorizontalSpacing};
  grid-template-columns:
    calc(${menuWidth} + 2 * ${menuHorizontalPadding})
    calc(
      100% -
        (
          ${menuWidth} + 2 * ${menuHorizontalPadding} +
            ${layoutHorizontalSpacing}
        )
    );
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    ". header"
    ". application"
    ". footer";
  width: 100%;
  height: 100%;
`;

const HeaderWrapper = styled.div`
  margin: 0 -${theme.spacing.spacing04} 0 -${layoutHorizontalSpacing};
  grid-area: header;
`;

const ApplicationWrapper = styled.div`
  grid-area: application;
  width: 100%;

  @media screen and (min-width: 2500px) {
    margin: 0 auto;
    max-width: 2500px;
  }
`;

const FooterWrapper = styled.div`
  grid-area: footer;
  padding-top: ${theme.spacing.spacing05};
`;
